import { Search, ArrowUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useGSCTopQueries } from '@/hooks/useGSCData';
import { useAuthStore } from '@/stores/auth-store';
import { useState } from 'react';

type SortField = 'query' | 'clicks' | 'impressions' | 'ctr' | 'position';
type SortDir = 'asc' | 'desc';

/**
 * Query Performance View - GSC queries with performance metrics
 * Shows top search queries with clicks, impressions, CTR, and position
 */
export function QueryPerformanceView() {
  const { activePropertyId, properties } = useAuthStore();
  const activeProperty = properties.find((p) => p.id === activePropertyId);
  const hasGSC = !!activeProperty?.gscSite;

  const { data: queries, isLoading } = useGSCTopQueries(50);
  const [sortField, setSortField] = useState<SortField>('clicks');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [searchFilter, setSearchFilter] = useState('');

  if (!hasGSC) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <div className="text-center">
          <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No Search Console property connected</p>
          <p className="text-sm mt-2">Connect a GSC property to see query performance</p>
        </div>
      </div>
    );
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const sortedQueries = queries
    ? [...queries]
        .filter((q) => q.query.toLowerCase().includes(searchFilter.toLowerCase()))
        .sort((a, b) => {
          const aVal = a[sortField];
          const bVal = b[sortField];
          const multiplier = sortDir === 'asc' ? 1 : -1;
          if (typeof aVal === 'string') {
            return aVal.localeCompare(bVal as string) * multiplier;
          }
          return ((aVal as number) - (bVal as number)) * multiplier;
        })
    : [];

  // Calculate aggregates
  const totals = sortedQueries.reduce(
    (acc, q) => ({
      clicks: acc.clicks + q.clicks,
      impressions: acc.impressions + q.impressions,
    }),
    { clicks: 0, impressions: 0 }
  );

  const avgCTR = totals.impressions > 0 ? (totals.clicks / totals.impressions) * 100 : 0;
  const avgPosition =
    sortedQueries.length > 0
      ? sortedQueries.reduce((sum, q) => sum + q.position, 0) / sortedQueries.length
      : 0;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Query Performance</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Analyze which search queries bring traffic to your site
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <SummaryCard
          label="Total Queries"
          value={sortedQueries.length.toString()}
          subtitle="Unique search terms"
          color="emerald"
        />
        <SummaryCard
          label="Total Clicks"
          value={totals.clicks.toLocaleString()}
          subtitle="From search results"
          color="emerald"
        />
        <SummaryCard
          label="Avg CTR"
          value={`${avgCTR.toFixed(1)}%`}
          subtitle="Click-through rate"
          color="emerald"
        />
        <SummaryCard
          label="Avg Position"
          value={avgPosition.toFixed(1)}
          subtitle="In search results"
          color="emerald"
        />
      </div>

      {/* Search Filter */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Filter queries..."
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg bg-surface border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </div>
        <p className="text-sm text-muted-foreground">
          Showing {sortedQueries.length} {sortedQueries.length === 1 ? 'query' : 'queries'}
        </p>
      </div>

      {/* Query Table */}
      <div className="glass-panel overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <SortableHeader
                  field="query"
                  label="Search Query"
                  currentField={sortField}
                  currentDir={sortDir}
                  onSort={handleSort}
                  className="text-left"
                />
                <SortableHeader
                  field="clicks"
                  label="Clicks"
                  currentField={sortField}
                  currentDir={sortDir}
                  onSort={handleSort}
                />
                <SortableHeader
                  field="impressions"
                  label="Impressions"
                  currentField={sortField}
                  currentDir={sortDir}
                  onSort={handleSort}
                />
                <SortableHeader
                  field="ctr"
                  label="CTR"
                  currentField={sortField}
                  currentDir={sortDir}
                  onSort={handleSort}
                />
                <SortableHeader
                  field="position"
                  label="Position"
                  currentField={sortField}
                  currentDir={sortDir}
                  onSort={handleSort}
                />
                <th className="px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider text-center">
                  Opportunity
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                    <div className="animate-pulse">Loading queries...</div>
                  </td>
                </tr>
              ) : sortedQueries.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                    No queries found
                  </td>
                </tr>
              ) : (
                sortedQueries.map((query, idx) => (
                  <QueryRow key={idx} query={query} />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

interface SortableHeaderProps {
  field: SortField;
  label: string;
  currentField: SortField;
  currentDir: SortDir;
  onSort: (field: SortField) => void;
  className?: string;
}

function SortableHeader({ field, label, currentField, currentDir: _currentDir, onSort, className }: SortableHeaderProps) {
  const isActive = currentField === field;

  return (
    <th
      className={cn(
        'px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-colors',
        className
      )}
      onClick={() => onSort(field)}
    >
      <div className={cn('flex items-center gap-1', !className?.includes('text-left') && 'justify-center')}>
        {label}
        <ArrowUpDown
          className={cn(
            'h-3 w-3 transition-colors',
            isActive ? 'text-primary' : 'text-muted-foreground/50'
          )}
        />
      </div>
    </th>
  );
}

interface SummaryCardProps {
  label: string;
  value: string;
  subtitle: string;
  color: 'emerald' | 'blue' | 'purple';
}

function SummaryCard({ label, value, subtitle, color }: SummaryCardProps) {
  const colorStyles = {
    emerald: 'border-l-emerald-500 bg-emerald-500/5',
    blue: 'border-l-blue-500 bg-blue-500/5',
    purple: 'border-l-purple-500 bg-purple-500/5',
  };

  return (
    <div className={cn('glass-panel p-4 border-l-4', colorStyles[color])}>
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="text-2xl font-bold text-foreground mt-1">{value}</p>
      <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
    </div>
  );
}

interface QueryRowProps {
  query: {
    query: string;
    clicks: number;
    impressions: number;
    ctr: number;
    position: number;
  };
}

function QueryRow({ query }: QueryRowProps) {
  // Determine opportunity type
  const opportunity = getOpportunity(query);

  return (
    <tr className="hover:bg-muted/30 transition-colors">
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-emerald-400 flex-shrink-0" />
          <span className="text-sm text-foreground font-medium truncate max-w-[300px]">
            {query.query}
          </span>
        </div>
      </td>
      <td className="px-4 py-3 text-center">
        <span className="text-sm font-medium text-foreground">{query.clicks.toLocaleString()}</span>
      </td>
      <td className="px-4 py-3 text-center">
        <span className="text-sm text-muted-foreground">{query.impressions.toLocaleString()}</span>
      </td>
      <td className="px-4 py-3 text-center">
        <span
          className={cn(
            'text-sm font-medium',
            query.ctr > 5 ? 'text-success' : query.ctr > 2 ? 'text-foreground' : 'text-warning'
          )}
        >
          {query.ctr.toFixed(1)}%
        </span>
      </td>
      <td className="px-4 py-3 text-center">
        <PositionBadge position={query.position} />
      </td>
      <td className="px-4 py-3 text-center">
        <OpportunityBadge opportunity={opportunity} />
      </td>
    </tr>
  );
}

function PositionBadge({ position }: { position: number }) {
  const rounded = Math.round(position * 10) / 10;

  let colorClass = 'bg-muted text-muted-foreground';
  if (position <= 3) {
    colorClass = 'bg-success/20 text-success';
  } else if (position <= 10) {
    colorClass = 'bg-blue-500/20 text-blue-400';
  } else if (position <= 20) {
    colorClass = 'bg-warning/20 text-warning';
  }

  return (
    <span className={cn('inline-flex px-2 py-0.5 rounded text-xs font-medium', colorClass)}>
      {rounded}
    </span>
  );
}

type OpportunityType = 'quick-win' | 'optimize' | 'maintain' | 'explore' | null;

function getOpportunity(query: {
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}): OpportunityType {
  // Quick Win: High impressions, good position (4-10), low CTR
  if (query.impressions > 100 && query.position >= 4 && query.position <= 10 && query.ctr < 3) {
    return 'quick-win';
  }
  // Optimize: Position 11-20 with decent impressions
  if (query.impressions > 50 && query.position > 10 && query.position <= 20) {
    return 'optimize';
  }
  // Maintain: Top 3 position with good CTR
  if (query.position <= 3 && query.ctr > 5) {
    return 'maintain';
  }
  // Explore: High impressions but very low CTR
  if (query.impressions > 500 && query.ctr < 1) {
    return 'explore';
  }
  return null;
}

function OpportunityBadge({ opportunity }: { opportunity: OpportunityType }) {
  if (!opportunity) {
    return <span className="text-xs text-muted-foreground">-</span>;
  }

  const styles: Record<Exclude<OpportunityType, null>, { bg: string; text: string; label: string }> = {
    'quick-win': { bg: 'bg-success/20', text: 'text-success', label: 'Quick Win' },
    optimize: { bg: 'bg-warning/20', text: 'text-warning', label: 'Optimize' },
    maintain: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Maintain' },
    explore: { bg: 'bg-purple-500/20', text: 'text-purple-400', label: 'Explore' },
  };

  const style = styles[opportunity];

  return (
    <span className={cn('inline-flex px-2 py-0.5 rounded text-xs font-medium', style.bg, style.text)}>
      {style.label}
    </span>
  );
}
